import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { ProductTypesWithoutBundle } from 'Types/ProductTypes'
import { useState, useEffect } from 'react'
import { useIsTrialSubscription } from './useIsTrialSubscription'
import { compareStrings } from 'Utils/stringHelper'

type IsProductTrialReturn = {
  allowed: boolean
  allowedProduct: ProductTypesWithoutBundle | null
  productsInTrial: ProductTypesWithoutBundle | null
  isProductAllowedTrial: (product?: ProductTypesWithoutBundle) => boolean
  isProductInTrial: (product?: ProductTypesWithoutBundle) => boolean
  isExtendTrialAllowed: (product?: ProductTypesWithoutBundle) => boolean
}

/**
 * Trial per product can active only if the user is not in signup trial and the product is allowed to be in trial
 *
 * @returns {IsProductTrialReturn}
 */
export const useIsProductTrial = (): IsProductTrialReturn => {
  const isTrialSubscription = useIsTrialSubscription()
  const [productInTrail, setProductInTrail] = useState<ProductTypesWithoutBundle | null>(null)
  const [allowedProduct, setAllowedProduct] = useState<ProductTypesWithoutBundle | null>(null)
  const [isProductTrialAllowed, setIsProductTrialActive] = useState<boolean>(false)
  const limits = useGetPackagingLimits()

  useEffect(() => {
    if (isTrialSubscription) {
      setIsProductTrialActive(false)
      setAllowedProduct(null)
      setProductInTrail(null)
    } else {
      const { trialDaysLeft, trialPerProduct, productName } = limits.data || {}
      const { allow } = trialPerProduct || {}
      const allowedProducts =
        Object.keys(allow || {})
          .filter((product) => allow?.[product as ProductTypesWithoutBundle])
          .toString()
          .toLowerCase() || null

      setIsProductTrialActive(Boolean(trialDaysLeft && productName))
      setAllowedProduct(allowedProducts as ProductTypesWithoutBundle | null)
      setProductInTrail(productName ?? null)
    }
  }, [limits, isTrialSubscription])

  const isProductAllowedTrial = (product?: ProductTypesWithoutBundle) =>
    !!allowedProduct?.includes(product as string)

  const isProductInTrial = (product?: ProductTypesWithoutBundle) => {
    if (!product || !productInTrail) return false
    return compareStrings(product, productInTrail)
  }

  const isExtendTrialAllowed = (product?: ProductTypesWithoutBundle) => {
    const extend = limits.data?.trialPerProduct?.extend

    if (!product || !extend) return false
    for (const key in extend) if (compareStrings(key, product)) return !!extend[key]
    return false
  }

  return {
    allowed: isProductTrialAllowed,
    allowedProduct: allowedProduct,
    productsInTrial: productInTrail,
    isProductAllowedTrial,
    isProductInTrial,
    isExtendTrialAllowed,
  }
}
